import { useEffect, useState } from 'react';
import {
  apiGetEmailMarketing,
  apiSaveEmailMarketing,
  apiTestEmailMarketing,
  type EmailMarketingView,
  type EmailProvider,
  type EmailTriggers,
} from '~/lib/api/email-marketing';
import { classNames } from '~/utils/classNames';
import {
  BTN_SECONDARY,
  CARD,
  ErrorBanner,
  Field,
  LoadError,
  Pill,
  Section,
  Select,
  SkeletonCard,
  Spinner,
  Switch,
} from '~/components/workbench/tools/payments/ui';
import { useResource } from '~/components/workbench/tools/payments/use-resource';
import {
  EMAIL_PROVIDER_SPECS,
  EMAIL_TRIGGERS,
  emailCredentials,
  emailProviderSpec,
  missingEmailFields,
  syncSourceLabel,
  syncStatusHint,
  syncStatusMeta,
} from './email-model';
import { apiErrorMessage, formatDate } from './marketing-model';

export function EmailView({ appId }: { appId: string }) {
  const view = useResource(() => apiGetEmailMarketing(appId), appId);

  if (view.loading && !view.data) {
    return (
      <Section title="Email marketing" icon="i-ph:envelope-open">
        <SkeletonCard lines={4} />
      </Section>
    );
  }

  if (!view.data) {
    return (
      <Section title="Email marketing" icon="i-ph:envelope-open">
        <LoadError message="Could not load email marketing." onRetry={() => void view.reload()} />
      </Section>
    );
  }

  return (
    <>
      <SettingsCard appId={appId} view={view.data} onSaved={view.setData} />
      <SyncLog view={view.data} reloading={view.loading} onReload={() => void view.reload()} />
    </>
  );
}

interface Draft {
  provider: EmailProvider | '';
  values: Record<string, string>;
  enabled: boolean;
  triggers: EmailTriggers;
}

function draftFrom(view: EmailMarketingView): Draft {
  return { provider: view.provider ?? '', values: { ...view.config }, enabled: view.enabled, triggers: view.triggers };
}

function SettingsCard({
  appId,
  view,
  onSaved,
}: {
  appId: string;
  view: EmailMarketingView;
  onSaved: (next: EmailMarketingView) => void;
}) {
  const [draft, setDraft] = useState<Draft>(() => draftFrom(view));
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [test, setTest] = useState<{ ok: boolean; message: string } | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => setDraft(draftFrom(view)), [view]);

  const provider = emailProviderSpec(draft.provider);
  const sameProvider = draft.provider === view.provider;
  // Credentials belong to one provider: switching starts clean on the server too.
  const storedSecrets = sameProvider ? view.secretsSet : [];
  const missing = provider ? missingEmailFields(provider, draft.values, storedSecrets) : [];
  const dirty =
    !sameProvider ||
    draft.enabled !== view.enabled ||
    EMAIL_TRIGGERS.some((t) => draft.triggers[t.key] !== view.triggers[t.key]) ||
    Boolean(
      provider?.fields.some((f) =>
        f.secret
          ? Boolean(draft.values[f.key]?.trim())
          : (draft.values[f.key] ?? '').trim() !== (view.config[f.key] ?? ''),
      ),
    );
  const canSave = Boolean(provider) && missing.length === 0 && dirty;

  const update = (patch: Partial<Draft>) => {
    setDraft((d) => ({ ...d, ...patch }));
    setSaved(false);
    setTest(null);
  };

  const chooseProvider = (id: string) => {
    const next = id as EmailProvider | '';
    update({ provider: next, values: next === view.provider ? { ...view.config } : {} });
  };

  const save = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!provider || !canSave) {
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const next = await apiSaveEmailMarketing(appId, {
        provider: provider.id,
        credentials: emailCredentials(provider, draft.values),
        triggers: draft.triggers,
        enabled: draft.enabled,
      });
      setSaved(true);
      onSaved(next);
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not save email marketing.'));
    } finally {
      setSaving(false);
    }
  };

  const runTest = async () => {
    setTesting(true);
    setTest(null);
    setError(null);

    try {
      setTest(await apiTestEmailMarketing(appId));
    } catch (err) {
      setTest({ ok: false, message: apiErrorMessage(err, 'Could not reach the provider.') });
    } finally {
      setTesting(false);
    }
  };

  return (
    <Section
      title="Email marketing"
      icon="i-ph:envelope-open"
      aside={
        view.configured ? (
          <Pill tone={view.enabled ? 'success' : 'muted'} label={view.enabled ? 'On' : 'Off'} />
        ) : undefined
      }
    >
      <form onSubmit={save} className={classNames(CARD, 'space-y-3.5 p-4')}>
        <p className="text-[12px] leading-relaxed text-secondary">
          Send leads, funnel opt-ins and customers to your email tool automatically.
        </p>

        <Field label="Provider" hint={provider?.description}>
          <Select value={draft.provider} onChange={chooseProvider} label="Provider">
            {!draft.provider && <option value="">Choose a provider</option>}
            {EMAIL_PROVIDER_SPECS.map((p) => (
              <option key={p.id} value={p.id}>
                {p.title}
              </option>
            ))}
          </Select>
        </Field>

        {provider && view.provider && !sameProvider && (
          <p className="text-[11px] leading-relaxed text-warning">
            Switching from {emailProviderSpec(view.provider)?.title ?? view.provider} replaces its saved credentials.
          </p>
        )}

        {provider?.fields.map((f) => {
          const stored = f.secret && storedSecrets.includes(f.key);

          return (
            <Field
              key={f.key}
              label={f.label}
              hint={f.secret ? 'Write-only. Stored encrypted and never shown again.' : undefined}
            >
              <div className="flex items-center gap-2">
                <input
                  type={f.secret ? 'password' : 'text'}
                  value={draft.values[f.key] ?? ''}
                  onChange={(e) => update({ values: { ...draft.values, [f.key]: e.target.value } })}
                  autoComplete="off"
                  spellCheck={false}
                  maxLength={500}
                  placeholder={stored ? 'Saved. Type to replace' : f.placeholder}
                  className="ui-input-mono min-w-0 flex-1"
                />
                {stored && <Pill tone="success" label="Saved" />}
              </div>
            </Field>
          );
        })}

        {provider && (
          <>
            <div className="space-y-2.5 border-t border-hairline pt-3">
              <Switch checked={draft.enabled} onChange={(enabled) => update({ enabled })} label="Sync contacts" />
              <div className="space-y-2 pl-1">
                {EMAIL_TRIGGERS.map((t) => (
                  <div key={t.key}>
                    <Switch
                      checked={draft.triggers[t.key]}
                      disabled={!draft.enabled}
                      onChange={(on) => update({ triggers: { ...draft.triggers, [t.key]: on } })}
                      label={t.label}
                    />
                    <p className="ml-11 text-[11px] text-muted">{t.hint}</p>
                  </div>
                ))}
              </div>
              <p className="text-[11px] leading-relaxed text-muted">
                Only people who ticked a marketing opt-in are subscribed. Customers and new accounts are added as
                contacts, and subscribed only if they opted in. Leads from the preview are never sent.
              </p>
            </div>
          </>
        )}

        {error && <ErrorBanner message={error} />}
        {test && (
          <div className={classNames(test.ok ? 'ui-banner-info' : 'ui-banner-error', 'items-start leading-relaxed')}>
            <div
              className={classNames(
                test.ok ? 'i-ph:check-circle text-success' : 'i-ph:warning-circle text-danger',
                'mt-px shrink-0 text-sm',
              )}
            />
            <span className={classNames('text-[12px]', test.ok ? 'text-secondary' : 'text-danger/90')}>
              {test.message}
            </span>
          </div>
        )}

        <div className="flex flex-wrap items-center gap-2">
          <button type="submit" disabled={saving || !canSave} className="ui-btn-accent !px-4 !py-2 !text-[12px]">
            {saving && <Spinner />}
            {saving ? 'Saving…' : 'Save'}
          </button>
          <button
            type="button"
            onClick={() => void runTest()}
            disabled={testing || saving || !view.configured || dirty}
            title={dirty ? 'Save first' : undefined}
            className={BTN_SECONDARY}
          >
            {testing ? <Spinner /> : <div className="i-ph:plugs-connected text-[13px]" />}
            Test connection
          </button>
          {provider && missing.length > 0 && (
            <span className="text-[11px] text-muted">Needs {missing.join(' and ')}</span>
          )}
          {saved && !dirty && <span className="text-[11px] text-muted">Saved</span>}
        </div>
      </form>
    </Section>
  );
}

function SyncLog({
  view,
  reloading,
  onReload,
}: {
  view: EmailMarketingView;
  reloading: boolean;
  onReload: () => void;
}) {
  const refresh = (
    <button onClick={onReload} disabled={reloading} className="ui-btn-icon" title="Refresh" aria-label="Refresh">
      {reloading ? <Spinner /> : <div className="i-ph:arrow-clockwise text-[13px] text-muted" />}
    </button>
  );

  return (
    <Section title="Recent syncs" icon="i-ph:clock-counter-clockwise" aside={refresh}>
      {view.lastSyncs.length === 0 ? (
        <div className={classNames(CARD, 'px-4 py-5 text-center')}>
          <p className="text-[12px] text-muted">Nothing synced yet. New contacts show up here as they arrive.</p>
        </div>
      ) : (
        <ul className={classNames(CARD, 'divide-y divide-hairline')}>
          {view.lastSyncs.map((row) => {
            const meta = syncStatusMeta(row.status);
            const hint = syncStatusHint(row.status);

            return (
              <li key={`${row.provider}:${row.sourceRef}`} className="space-y-1 px-4 py-2.5">
                <div className="flex items-center justify-between gap-3">
                  <span className="min-w-0 truncate text-[12px] text-primary">{row.email}</span>
                  <Pill tone={meta.tone} label={meta.label} />
                </div>
                <div className="text-[11px] text-muted">
                  {[
                    emailProviderSpec(row.provider)?.title ?? row.provider,
                    syncSourceLabel(row.sourceRef),
                    formatDate(row.createdAt),
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </div>
                {row.status === 'failed' && row.lastError && (
                  <div className="text-[11px] leading-relaxed text-danger/90">{row.lastError}</div>
                )}
                {hint && <div className="text-[11px] text-muted">{hint}</div>}
              </li>
            );
          })}
        </ul>
      )}
    </Section>
  );
}
